import type { CrucibleSessionConnection, DisplaySettings, FallenChoice, GameplayInputBindingId, Vector2 } from '@sorrem/shared';
import type { CleanRenderResult, GameMapSnapshot, SoundCommand, UIDebugOverlaySettings, UIGameState, UIItemSlotRef } from '@sorrem-client/types';
import type { GatheringEntranceSpot, RenderWorkerCommand } from './render-config';

/** The scene commands the gathering page sends while the party gathers (see crucible-scene.ts). */
export type CrucibleGatheringCommand = Extract<RenderWorkerCommand, { readonly type: `crucible-gathering-${string}` | 'preload-models' }>;

/** Tags the gathering's own `preload-models`, so their answers reach the page, never a game that has since started. */
export const GATHERING_PRELOAD_TAG = 'gathering';

/**
 * The message contract between the page (React, DOM, input capture) and the
 * running game (socket, prediction, scene). Everything the two sides say to
 * each other goes through these types, so the game can run on whichever
 * thread hosts it. All payloads are structured-clone safe: no functions, no
 * class instances.
 */

/** The parts of the page's `window.location` the game needs to resolve its socket URL. */
export interface GameLocation {
    readonly origin: string;
    readonly protocol: string;
    readonly hostname: string;
    readonly port: string;
}

/** Everything the game needs to start, resolved by the page from the DOM and session storage. */
export interface GameStartOptions {
    readonly sessionConnection: CrucibleSessionConnection;
    /** The account's API session JWT; the crucible host authenticates the gameplay socket with it. */
    readonly sessionToken: string;
    readonly characterId: string;
    readonly location: GameLocation;
    readonly debugOverlaySettings: UIDebugOverlaySettings;
    /** The account's display settings: damage numbers and essence bars (#200, #202). */
    readonly displaySettings: DisplaySettings;
    /** The account's camera zoom level, which the run starts at. */
    readonly cameraZoomLevel: number;
}

export type StartCraftResult = { readonly ok: true } | { readonly ok: false; readonly reason: string };

/** The answer to a craft request when there is no game to craft with. */
export const START_CRAFT_UNAVAILABLE: StartCraftResult = { ok: false, reason: 'Crafting is unavailable right now.' };

/** Page -> game. */
export type GameCommand =
    /**
     * A gameplay binding started (its input went down, or a modifier change
     * made it the one that applies - then `isPress` is false). `position` is
     * the cursor in canvas drawing-buffer pixels. The page has already applied
     * bindings and focus: the game only sees intents, never keys or buttons.
     */
    | { readonly type: 'intent-start'; readonly intent: GameplayInputBindingId; readonly position: Vector2; readonly isPress: boolean }
    | { readonly type: 'intent-end'; readonly intent: GameplayInputBindingId }
    /** The cursor moved over the game canvas, in canvas drawing-buffer pixels. */
    | { readonly type: 'pointer-move'; readonly position: Vector2 }
    /** Continuous movement direction (joystick or gamepad stick), zero when released. */
    | { readonly type: 'move-direction'; readonly direction: Vector2 }
    /** The camera's zoom level changed: the mouse wheel, or the settings slider (see `GameStateBridge.wheelCameraZoom`). */
    | { readonly type: 'set-camera-zoom'; readonly level: number }
    /** The display canvas's new drawing-buffer size, in device pixels. */
    | { readonly type: 'resize'; readonly width: number; readonly height: number }
    | { readonly type: 'set-debug-overlay-settings'; readonly settings: UIDebugOverlaySettings }
    | { readonly type: 'set-display-settings'; readonly settings: DisplaySettings }
    /** The player's reduced-motion preference (`prefers-reduced-motion`), which a worker can't read itself. */
    | { readonly type: 'set-reduced-motion'; readonly isReducedMotion: boolean }
    | {
        readonly type: 'request-inventory-move';
        readonly callId: number;
        readonly requestId: string;
        readonly itemId: string;
        readonly target: UIItemSlotRef;
    }
    | { readonly type: 'request-consume-crystal'; readonly itemId: string }
    | { readonly type: 'request-drop-item'; readonly itemId: string }
    | { readonly type: 'request-cancel-crystal-use' }
    | { readonly type: 'request-cast-ability'; readonly skillId: string }
    | { readonly type: 'request-cancel-cast-ability' }
    | { readonly type: 'request-leave-run' }
    | { readonly type: 'request-fallen-choice'; readonly choice: FallenChoice }
    | { readonly type: 'request-tour-hold'; readonly isActive: boolean }
    | {
        readonly type: 'request-start-craft';
        readonly callId: number;
        readonly slotIndex: number;
        readonly recipeId: string;
    }
    | { readonly type: 'request-cancel-craft'; readonly coalescenceId: number };

/** The cursor the pointer should show over the game canvas. */
export type GameCursorMode = 'default' | 'attack' | 'interact';

/** Game -> page. */
export type GameMessage =
    /** Changed top-level fields of the UI state, coalesced since the last patch. */
    | { readonly type: 'ui-state'; readonly patch: Partial<UIGameState> }
    | { readonly type: 'ui-map'; readonly map: GameMapSnapshot | null }
    | { readonly type: 'cursor'; readonly mode: GameCursorMode }
    | { readonly type: 'inventory-move-result'; readonly callId: number; readonly accepted: boolean }
    | { readonly type: 'start-craft-result'; readonly callId: number; readonly result: StartCraftResult }
    /** A sound for the page to play: the game's scene (and the gathering's) can't, from a worker. */
    | { readonly type: 'sound'; readonly command: SoundCommand };

/**
 * Page -> game worker. `start` is always the first message and carries the
 * display canvas (transferred); `stop` asks the game to shut down cleanly;
 * everything in between is a `GameCommand`.
 */
export type GameWorkerRequest =
    | { readonly type: 'start'; readonly canvas: OffscreenCanvas; readonly options: GameStartOptions }
    /**
     * A crucible page's first request: build the crucible scene (the
     * gathering, with the run to follow in the same scene) on `canvas`,
     * before there is a run to start. Without `withGathering` (a solo
     * direct-entry run) the scene only loads the run.
     */
    | { readonly type: 'start-gathering'; readonly canvas: OffscreenCanvas; readonly withGathering: boolean }
    | { readonly type: 'gathering-command'; readonly command: CrucibleGatheringCommand }
    /** After `start-gathering`: the host is up, so start the game on the scene already running. */
    | { readonly type: 'start-run'; readonly options: GameStartOptions }
    | { readonly type: 'stop' }
    /** A developer snapshot (#303): the live scene, gathering or run, drawn alone at `scale` times the canvas size. */
    | { readonly type: 'capture-clean-render'; readonly callId: number; readonly scale: number }
    | GameCommand;

/** Game worker -> page: the game's own messages, plus `stopped` once it has shut down. */
export type GameWorkerMessage =
    | GameMessage
    | { readonly type: 'stopped' }
    /** The crucible scene failed while no game was running yet to report it. */
    | { readonly type: 'scene-error'; readonly message: string }
    /** A gathering `preload-models` (tagged `GATHERING_PRELOAD_TAG`) finished. */
    | { readonly type: 'run-models-ready' }
    /** The gathering set is built for its crucible, so it has something to show; `entrance` places and sizes the entry countdown (see render-config.ts). */
    | { readonly type: 'gathering-ready'; readonly entrance: GatheringEntranceSpot }
    /** The answer to `capture-clean-render`; `result.png` is transferred. */
    | { readonly type: 'clean-render-result'; readonly callId: number; readonly result: CleanRenderResult }
    /** An uncaught error or unhandled rejection in the worker, for the page's crash report (#212). */
    | { readonly type: 'worker-error'; readonly message: string; readonly stack?: string };
